import React, { useContext, useEffect, useState } from 'react'
import { Layout } from './Layout'
import { t } from '../i18n/i18n'
import { Overview } from '../components/common/overview/Overview'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RiskManagementTabs, deleteRiskAnalysis, getRiskManagementColumns, isRiskAnalysisOwnerOrCreator } from '../helpers/riskManagementHelper'
import { ShieldAdd24Regular } from '@fluentui/react-icons'
import { Button, Tab, makeStyles } from '@fluentui/react-components'
import { RiskAnalysis, RiskAnalysisStatus, SubscriptionLevel, Workplace } from '../api/schemas/schema'
import { api } from '../helpers/apiHelper'
import { IListState } from '../interfaces/IListState'
import { DataContext } from '../context/DataContext'
import { ModalRiskAssesment } from '../components/riskAnalyses/ModalRiskAssesment'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { NavigationLink } from '../interfaces/INavigationLink'
import { isSystemAdmin } from '../helpers/permissionHelper'
const useStyles = makeStyles({
   addBtn: {
      '@media screen and (max-width: 640px)': {
         display: 'inline-flex',
      },
   },
   hiddenInPhone: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'inline',
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '50%',
         },
         ':nth-child(2)': {
            width: '20%',
            minWidth: '150px',
         },
         ':nth-child(3)': {
            width: '20%',
            minWidth: '200px',
         },
         ':nth-child(4)': {
            width: '10%',
            minWidth: '150px',
         },
      },
   },
})
export const RiskManagement = () => {
   const dataContext = useContext(DataContext)
   const { organisation, userAccount } = dataContext.state
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')
   const classes = useStyles()
   const navigate = useNavigate()
   const [riskManagementState, setRiskManagementState] = useState<IListState<RiskAnalysis>>({
      loading: true,
      filteredItems: [],
      allItems: [],
      open: false,
   })
   const { loading, filteredItems, allItems } = riskManagementState
   const [workplaces, setWorkplaces] = useState<Workplace[]>([])

   useEffect(() => {
      if (!selectedTab) {
         setSearchParams({ tab: RiskManagementTabs.Ongoing })
         return
      }
      if (!organisation?.id) return
      const ac = new AbortController()
      ;(async () => {
         try {
            const [riskAnalysesRes, workplacesRes] = await Promise.all([
               api().getRiskAnalysesByOrganisationId(organisation.id, { signal: ac.signal }),
               api().getWorkplacesByOrganisationId(organisation.id, { signal: ac.signal }),
            ])
            const allItems = riskAnalysesRes.data ?? []
            const filteredItems =
               selectedTab === RiskManagementTabs.Ongoing
                  ? allItems.filter((x) => x.status !== RiskAnalysisStatus.Completed)
                  : allItems.filter((x) => x.status === RiskAnalysisStatus.Completed)
            setWorkplaces(workplacesRes.data)
            setRiskManagementState((prev) => ({ ...prev, loading: false, allItems, filteredItems }))
         } catch (error: any) {
            if (error?.name !== 'AbortError') throw error
         }
      })()
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedTab, dataContext.state.userAccount?.id, organisation?.id])

   useEffect(() => {
      if (selectedTab === RiskManagementTabs.Ongoing)
         setRiskManagementState((prev) => ({ ...prev, filteredItems: prev.allItems.filter((x) => x.status !== RiskAnalysisStatus.Completed) }))
      if (selectedTab === RiskManagementTabs.Concluded)
         setRiskManagementState((prev) => ({ ...prev, filteredItems: prev.allItems.filter((x) => x.status === RiskAnalysisStatus.Completed) }))
   }, [selectedTab, allItems.length])

   return (
      <Layout
         activeDesktopLink={NavigationLink.RiskManagement}
         breadCrumbItems={[{ title: t('RiskManagement'), active: true, path: NavigationLink.RiskManagement }]}
         tabs={
            <>
               <Tab value={RiskManagementTabs.Ongoing}>{t('Ongoing')}</Tab>
               <Tab value={RiskManagementTabs.Concluded}>{t('Concluded')}</Tab>
            </>
         }
         button={
            <Button
               className={classes.addBtn}
               icon={<ShieldAdd24Regular />}
               appearance="primary"
               onClick={() => setRiskManagementState((prev) => ({ ...prev, open: true }))}
            >
               {t('NewRiskAssesment')}
            </Button>
         }
      >
         <Overview
            loading={loading}
            columns={getRiskManagementColumns(setRiskManagementState, userAccount)}
            items={filteredItems}
            onRowClick={(item) => navigate(`/risk-management/${item.id}`)}
            keyValue="name"
            noItemsText={selectedTab === RiskManagementTabs.Ongoing ? t('TimeToCreateYourFirstRiskAssesment') : t('NoConcludedRiskAssesments')}
            title={selectedTab === RiskManagementTabs.Ongoing ? t('OngoingRiskAssesments') : t('ConcludedRiskAssesments')}
            rowClassName={classes.row}
            renderMenuButton={(item) => (
               <MoreOptionsMenuButton
                  disabled={!isRiskAnalysisOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
                  disabledToolTipMessage={t('DeleteRiskAnalysisNoAccessMessage')}
                  item={item}
                  setState={setRiskManagementState}
                  deleteMessage={t('DeleteRiskAssessment')}
                  deleteConfirmationMessage={t('DeleteRiskAssessmentConfirmationMessage')}
                  deleteFunction={deleteRiskAnalysis}
               />
            )}
         />
         <ModalRiskAssesment setRiskManagementState={setRiskManagementState} riskManagementState={riskManagementState} workplaces={workplaces} />
      </Layout>
   )
}
